import React, { useEffect, useState } from "react";
import "font-awesome/css/font-awesome.min.css";
import FatherFormDialog from "./FatherFormDialog";
import { Button, Grid, IconButton } from "@mui/material";
import { Add, AttachFile, Delete, Edit } from "@mui/icons-material";
import { useForm, useFormContext } from "react-hook-form";
import FilePreviewer from "qubCommon/FilePreviewer";
import { useYupValidationResolver } from "hooks/useYupValidationResolver";
import { useFatherValidation } from "util/validations/father-form";
import { deleteFile, getFiles, uploadFiles } from "apis/controllers/files";
import { v4 as uuidv4 } from "uuid";
import { downloadFile } from "util";
import { useLookup } from "context/lookupContext";
import "./Fathers.css";

const FathersTable = ({
  husbands,
  canEdit = false,
  canAdd = false,
  canDelete = false,
  canPartiallyEdit,
}) => {
  const [data, setData] = useState(husbands ?? []);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);

  const fatherValidation = useFatherValidation();
  const resolver = useYupValidationResolver(fatherValidation);
  const fatherForm = useForm({ resolver });
  const form = useFormContext();
  const { lookups } = useLookup();

  useEffect(() => {
    setData(husbands);
    form.setValue("husbandsHelp", husbands, {
      shouldDirty: false,
    });
    form.setValue("husbands", null, { shouldDirty: false });
    form.setValue("husbands.created", [], { shouldDirty: false });
    form.setValue("husbands.updated", [], { shouldDirty: false });
    form.setValue("husbands.deleted", [], { shouldDirty: false });
  }, [husbands]);

  const handleCloseDialog = () => {
    fatherForm.reset({});
    setOpenDialog(false);
  };

  const sortTable = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    let sortedData;
    if (["relationship"].includes(key)) {
      sortedData = [...data].sort((a, b) => {
        const aValue = a[key]?.name || "";
        const bValue = b[key]?.name || "";
        if (aValue < bValue) {
          return direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    } else {
      sortedData = [...data].sort((a, b) => {
        if (a[key] < b[key]) {
          return direction === "ascending" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    setData(sortedData);
  };

  const getIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        return <i className="fas fa-sort-up"></i>;
      } else {
        return <i className="fas fa-sort-down"></i>;
      }
    } else {
      return <i className="fas fa-sort"></i>;
    }
  };

  const handleAddRow = async (father) => {
    form.setValue("husbandsHelp", [
      ...data,
      { ...father, position: data?.length + 1 },
    ]);
    setData((prev) => [...prev, { ...father, position: prev?.length + 1 }]);
    setOpenDialog(false);
    fatherForm.reset({});
    const createdFathers = form.getValues("husbands.created") ?? [];
    form.setValue("husbands.created", [...createdFathers, father], {
      shouldDirty: true,
    });
  };

  const handleBeginEdit = (father) => {
    fatherForm.reset(father);
    setOpenDialog(true);
  };

  const handleDeleteRow = (father) => {
    if (
      window.confirm(
        `Είστε σίγουροι οτι θέλετε να διαγράψετε τον γονέα/πρόσωπο #${father?.position}`
      )
    ) {
      if (father?.isNew) {
        const createdFathers = form.getValues("husbands.created");
        form.setValue(
          "husbands.created",
          createdFathers.filter((ap) => ap.id !== father?.id),
          { shouldDirty: true }
        );
      } else {
        const updatedFathers = form.getValues("husbands.updated");
        form.setValue(
          "husbands.updated",
          updatedFathers.filter((ap) => ap.id !== father?.id),
          { shouldDirty: true }
        );
        const deletedFathers = form.getValues("husbands.deleted") ?? [];
        form.setValue("husbands.deleted", [...deletedFathers, father?.id], {
          shouldDirty: true,
        });
      }
      setData((prev) => {
        let returnData = prev;
        returnData = prev.filter((row) => row.id !== father.id);
        form.setValue("husbandsHelp", returnData, {
          shouldDirty: false,
        });
        return returnData.map((row) =>
          row?.position > father?.position
            ? { ...row, position: row?.position - 1 }
            : row
        );
      });
    }
  };

  const handleEditRow = async (father) => {
    form.setValue(
      "husbandsHelp",
      data.map((row) => (row.id === father.id ? { ...row, ...father } : row))
    );
    setData((prev) =>
      prev.map((row) => (row.id === father.id ? { ...row, ...father } : row))
    );
    const editedFathers = form.getValues("husbands.updated") ?? [];

    if (father?.isNew) {
      const createdFathers =
        form
          .getValues("husbands.created")
          ?.filter((ap) => ap.id !== father.id) ?? [];
      form.setValue("husbands.created", [...createdFathers, father], {
        shouldDirty: true,
      });
    } else
      form.setValue("husbands.updated", [...editedFathers, father], {
        shouldDirty: true,
      });
    setOpenDialog(false);
    fatherForm.reset({});
  };

  const handlePreviewFile = async (file, newFile = false) => {
    if (newFile) setFileToPreview(file);
    else {
      const res = await downloadFile(file?.id, file?.type ?? file?.contentType);
      setFileToPreview(res);
    }
  };

  const handleDeleteFile = async (delFile, id, bundleId) => {
    const res = await deleteFile({
      fileId: delFile?.id,
      bundleId,
      recordGuid: id ?? null,
      projectTableId: 5,
    });
    const createdAppartments = form.getValues("husbands.created");
    const updatedAppartments = form.getValues("husbands.updated");

    form.setValue(
      "husbands.created",
      createdAppartments.map((ap) =>
        ap.id === id ? { ...ap, bundles: [] } : ap
      ),
      { shouldDirty: true }
    );
    form.setValue(
      "husbands.updated",
      updatedAppartments.map((ap) =>
        ap.id === id ? { ...ap, bundles: [] } : ap
      ),
      { shouldDirty: true }
    );
    setData((prevData) =>
      prevData.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            files: item.files.filter((file) => file.id !== delFile?.id),
          };
        }
        return item;
      })
    );
    return res?.status;
  };

  let applicantAmka;

  try {
    applicantAmka = form.getValues("applicant")?.amka;
  } catch (e) {
    console.log(e);
  }

  return (
    <>
      {fileToPreview && (
        <FilePreviewer
          file={fileToPreview}
          onClose={() => setFileToPreview(null)}
        />
      )}
      <FatherFormDialog
        canEdit={canEdit}
        canPartiallyEdit={canPartiallyEdit}
        open={openDialog}
        handleAddRow={handleAddRow}
        handleClose={handleCloseDialog}
        fatherForm={fatherForm}
        handleEditRow={handleEditRow}
        handlePreviewFile={handlePreviewFile}
        handleDeleteFile={handleDeleteFile}
        applicantAmka={applicantAmka}
      />

      {canAdd && (
        <Grid container item xs={12} sx={{ paddingBottom: 2 }}>
          <Button
            sx={{ marginLeft: "auto" }}
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              fatherForm.reset();

              setOpenDialog(true);
            }}
          >
            Προσθήκη
          </Button>
        </Grid>
      )}
      <div style={{ width: "100%", overflowX: "auto" }}>
        <table className="application-input-table">
          <thead>
            <tr>
              <th align="center" onClick={() => sortTable("position")}>
                Α/Α {getIcon("position")}
              </th>
              <th onClick={() => sortTable("amka")}>ΑΜΚΑ {getIcon("amka")}</th>
              <th onClick={() => sortTable("ama")}>
                Α.Μ.ΟΓΑ/ΕΦΚΑ {getIcon("ama")}
              </th>
              <th align="" onClick={() => sortTable("lastName")}>
                Ονοματεπώνυμο {getIcon("lastName")}
              </th>
              {/* <th align="center" onClick={() => sortTable("firstName")}>
                Όνομα {getIcon("firstName")}
              </th> */}

              <th align="center" onClick={() => sortTable("relationship")}>
                Σχέση με δικαιούχο {getIcon("relationship")}
              </th>

              <th />
            </tr>
          </thead>
          <tbody>
            {data?.map((row, index) => (
              <tr key={index}>
                <td align="center">{row?.position}</td>
                <td>{row?.amka}</td>
                <td>{row?.ama}</td>
                <td align="">
                  {row?.lastName} {row?.firstName}
                </td>

                {/* <td align="center">{row?.firstName}</td> */}
                <td align="center">{row?.relationship?.name}</td>

                <td>
                  <div className="action-buttons">
                    {(canEdit || canPartiallyEdit) && (
                      <button
                        onClick={() => handleBeginEdit(row)}
                        className="btn btn-edit-small"
                      >
                        <Edit fontSize="small" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDeleteRow(row)}
                        className="btn btn-edit-small"
                      >
                        <Delete fontSize="small" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default FathersTable;
